import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { ArrowRight } from "@phosphor-icons/react/dist/ssr";
import { qoHomeCurrent } from "../../utils/queryOptions";
import { THomeState } from "../../types/homeTypes";
import { useAuth } from "../../hooks/useAuth";
import { myAxios } from "../../utils/common";
import ModuleTitle from "./ModuleTitle";
import CurrentTasks from "./CurrentTasks";
import DashboardHero from "./DashboardHero";
import DemoCountdownCard from "./DemoCountdownCard";
import CapstoneReadyCard from "./CapstoneReadyCard";
import { ScoreMap } from "./CurrentWeek";
import ModuleQuizCard from "../moduleQuiz/ModuleQuizCard";

const HEADINGS: Record<THomeState, string> = {
  IN_PROGRESS: "Kaldığın yerden devam et",
  MODULE_DONE: "Tebrikler! Sıradaki modül hazır 🎉",
  NEW: "Hadi başlayalım 🚀",
  ALL_DONE: "Müfredatı tamamladın 🏆",
  EMPTY: "Henüz atanmış içerik yok",
};

export default function CurrentModuleView() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery(qoHomeCurrent());

  // Hero istatistikleri + görev rozetleri için skorları çek (CurrentWeek ile aynı kalıp).
  const bulkItems = data?.currentModule
    ? data.currentModule.contents
        .filter((c) => c.tag !== "LIVE_EVENT")
        .map((c) => ({
          content_id: c.content_id,
          type: c.tag === "UNIT" ? "PRACTICE" : c.tag,
          name: c.tag === "PROJECT" ? c.infos?.title || "" : c.name,
          tag: c.tag,
        }))
    : [];

  const { data: scoreMap } = useQuery({
    queryKey: ["bulkScores", bulkItems.map((i) => i.content_id)],
    queryFn: () =>
      myAxios
        .post<ScoreMap>("/cache/bulk", { items: bulkItems })
        .then((r) => r.data),
    enabled: bulkItems.length > 0,
    staleTime: 30 * 1000,
  });

  return (
    <>
      <DemoCountdownCard cohortName={user?.cohort_name} demoEndDate={user?.demo_end_date} />
      {data?.capstoneReady && (
        <div className="mb-6">
          <CapstoneReadyCard />
        </div>
      )}
      <DashboardHero scoreMap={scoreMap} bulkItems={bulkItems} context="module" />

      <div className="box mt-4 rounded-3xl overflow-hidden">
        <div className="p-4 sm:p-6">
          {isLoading ? (
            <div className="text-white/40 text-sm py-4">Yükleniyor…</div>
          ) : (
            <>
              <h2 className="text-lg sm:text-xl font-semibold mb-1">
                {HEADINGS[data?.state ?? "EMPTY"]}
              </h2>

              {data?.currentModule ? (
                <>
                  <div className="mt-3 mb-1">
                    <ModuleTitle cc={data.currentModule} showCompletion={true} />
                    <CurrentTasks
                      currentModule={data.currentModule}
                      scoreMap={scoreMap}
                      resumeId={data.resumeUnitId}
                    />
                  </div>

                  <ModuleQuizCard moduleId={data.currentModule.module_id} />

                  {data?.nextModule && (
                    <p className="mt-4 text-sm text-white/40">
                      Sıradaki: Modül {data.nextModule.order} · {data.nextModule.title}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-white/40 text-sm py-4">
                  Sana atanmış bir modül bulunamadı.
                </p>
              )}

              <div className="text-right mt-3">
                <Link
                  to="/hepsi"
                  className="inline-flex text-white/50 hover:text-myGreen font-semibold items-center gap-1.5 py-2 px-3 rounded-lg hover:bg-white/5 transition-all text-sm"
                >
                  <span>Tüm Dersler</span>
                  <ArrowRight size="16" weight="bold" />
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
